import { invokeAPI } from '@shared/invokeAPI';
import { ScheduleState } from '@shared/state/schedule';
import { state } from '@shared/state/store';
import { updateStore } from '@shared/state/updateStore';
import { DateTimeString, RawScheduledScreen, ScheduledScreen } from '@shared/types';
import { addSeconds, differenceInSeconds, startOfDay, isBefore, isEqual } from 'date-fns';
import { poll } from './PollingService';
import { ScreenRenderers } from './ScreenRegistry';

export function pollSchedule(onChange?: (current: ScheduledScreen) => unknown): () => void {
  tick(onChange);
  return poll(fetchSchedule);
}

function tick(onChange?: (current: ScheduledScreen) => unknown): void {
  const { schedule, current } = state.schedule;
  const newCurrent = findCurrent(schedule, new Date());

  const hasActionChanged = newCurrent?.action !== current?.action;
  const hasContentChanged = newCurrent?.data  !== current?.data;
  const hasChanged = hasActionChanged || hasContentChanged;

  if (hasChanged) {
    updateState({ current: newCurrent });
    onChange?.(newCurrent);
  }

  setTimeout(() => tick(onChange), 1000);
}

function fetchSchedule(): Promise<Array<ScheduledScreen> | void> {
  const screen = location.hash.substring(1);
  updateState({ isBusy: true });
  return (
    invokeAPI<Array<RawScheduledScreen>>('getSchedule', {'[screen]': screen})
      .then(({ data }) => {
        const schedule = parseSchedule(data);
        updateState({ isBusy: false, schedule });
        return schedule;
      })
      .catch(handleError)
  );
}

function findCurrent(schedule: Array<ScheduledScreen>, date: Date): ScheduledScreen | undefined {
  const overlapping = schedule.filter(({ start, end, action }) => (
    action in ScreenRenderers
    &&
    (isEqual(start, date) || isBefore(start, date))
    &&
    (isEqual(end, date) || isBefore(date, end))
  ));
  const ordered = sortScheduleByDistanceFromNow(overlapping);
  const [ current ] = ordered;
  console.log({ overlapping, ordered, current })
  return current;
}

function parseSchedule(raw: Array<RawScheduledScreen>): Array<ScheduledScreen> {
  const parsed = raw.map(({ startDate, endDate, startTime, endTime, action, data }) => {
    const startDay = startOfDay(new Date(startDate));
    const endDay = startOfDay(new Date(endDate));
    const start = addSeconds(startDay, getOffsetInSeconds(startTime));
    const end = addSeconds(endDay, getOffsetInSeconds(endTime));
    const sanitizedEnd = isBefore(end, start) ? start : end;
    return { action, data, start, end: sanitizedEnd };
  });
  return sortSchedule(parsed);
}

function sortSchedule(schedule: Array<ScheduledScreen>): Array<ScheduledScreen> {
  return schedule.sort((a, b) => {
    const startOfA = a.start.getTime();
    const startOfB = b.start.getTime();
    if (startOfA < startOfB) {
      return -1;
    }
    if (startOfA > startOfB) {
      return 1;
    }
    return 0;
  });
}

function sortScheduleByDistanceFromNow(schedule: Array<ScheduledScreen>): Array<ScheduledScreen> {
  const now = Date.now();
  return schedule.sort((a, b) => {
    const timeToA = now - a.start.getTime();
    const timeToB = now - b.start.getTime();
    if (timeToA < timeToB) {
      return -1;
    }
    if (timeToA > timeToB) {
      return 1;
    }
    return 0;
  });
}

function getOffsetInSeconds(dateTime: DateTimeString): number {
  const end = new Date(dateTime);
  const start = startOfDay(end);
  return differenceInSeconds(end, start);
}

function handleError(error: any): void {
  updateState({ isBusy: false, error: String(error) });
}

function updateState(updates: Partial<ScheduleState>): void {
  updateStore('schedule', updates);
}
